import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { BrandLogoLink } from '../components/BrandLogoLink';
import { LoadingSpinner } from '../components/LoadingSpinner';

/**
 * Supabase "Forgot password" email links to this page with a recovery session in the URL hash.
 */
export function UpdatePasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [noSession, setNoSession] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (!cancelled) {
          setSessionReady(true);
          setNoSession(false);
        }
      }
    });
    void (async () => {
      for (let i = 0; i < 12; i++) {
        if (cancelled) return;
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          if (!cancelled) setSessionReady(true);
          return;
        }
        await new Promise((r) => setTimeout(r, 200));
      }
      if (cancelled) return;
      setNoSession(true);
    })();
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Use at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) {
        setError(updateErr.message);
        return;
      }
      await navigate('/library', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  if (!sessionReady && !noSession) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-6 text-th-secondary">
        <BrandLogoLink />
        <div className="flex flex-col items-center gap-3">
          <LoadingSpinner size="md" />
          <p className="text-sm">Preparing password reset…</p>
        </div>
      </div>
    );
  }

  if (noSession) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-6 text-center">
        <BrandLogoLink />
        <div className="flex w-full max-w-md flex-col items-center gap-4">
          <p className="text-th-secondary">This reset link is invalid or expired.</p>
          <Link to="/login" className="text-brand hover:underline">
            Sign in
          </Link>
          <p className="text-xs text-th-tertiary">
            <Link to="/" className="hover:text-th-secondary">
              Back to home
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-6">
      <BrandLogoLink />
      <div className="w-full max-w-md space-y-8 rounded-2xl border border-subtle bg-elevated p-8 shadow-xl">
        <div>
          <h1 className="text-2xl font-semibold text-th-primary">Set a new password</h1>
          <p className="mt-2 text-sm text-th-secondary">Choose a password for your account.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-th-primary">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-subtle bg-base px-3 py-2 text-field focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-th-primary">Confirm password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full rounded-lg border border-subtle bg-base px-3 py-2 text-field focus:border-brand focus:outline-none"
            />
          </div>
          {error ? (
            <p className="text-sm text-status-error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-brand py-2.5 font-medium text-surface hover:bg-brand-hover disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Update password'}
          </button>
        </form>
        <p className="text-center text-xs text-th-tertiary">
          <Link to="/" className="hover:text-th-secondary">
            Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
